import { useCallback, useEffect, useRef } from 'react';

import { AI_BUILDER_CHAT_DRAWER_TOP_OFFSET } from '../components/AiBuilderShellTransition';

type MobileDrawerViewport = {
  bottom: number;
  top: number;
};

const DEFAULT_MOBILE_DRAWER_VIEWPORT: MobileDrawerViewport = {
  bottom: 0,
  top: AI_BUILDER_CHAT_DRAWER_TOP_OFFSET,
};

const getMobileDrawerViewport = (): MobileDrawerViewport => {
  const visualViewport = window.visualViewport;

  if (!visualViewport || visualViewport.height <= 0) {
    return DEFAULT_MOBILE_DRAWER_VIEWPORT;
  }

  const offsetTop = Math.max(visualViewport.offsetTop, 0);

  return {
    bottom: Math.max(window.innerHeight - offsetTop - visualViewport.height, 0),
    top: Math.max(AI_BUILDER_CHAT_DRAWER_TOP_OFFSET, offsetTop),
  };
};

export const useAiBuilderMobileDrawerViewport = (enabled: boolean) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const updateViewport = useCallback(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;

    const viewport = enabled
      ? getMobileDrawerViewport()
      : DEFAULT_MOBILE_DRAWER_VIEWPORT;

    // 키보드 애니메이션 중 채팅 콘텐츠 전체가 다시 렌더되지 않게 한다.
    drawer.style.bottom = `${viewport.bottom}px`;
    drawer.style.top = `${viewport.top}px`;
  }, [enabled]);
  const setDrawerRef = useCallback(
    (drawer: HTMLDivElement | null) => {
      drawerRef.current = drawer;
      updateViewport();
    },
    [updateViewport],
  );

  useEffect(() => {
    updateViewport();
    if (!enabled) return;

    const visualViewport = window.visualViewport;

    window.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('resize', updateViewport);
    visualViewport?.addEventListener('scroll', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('resize', updateViewport);
      visualViewport?.removeEventListener('scroll', updateViewport);
    };
  }, [enabled, updateViewport]);

  return {
    drawerRef,
    setDrawerRef,
  };
};
